import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/Advice_logo_EN.png';
import UserMenu from './UserMenu';
import BottomEdgeDivider from '../components/shared/common/BottomEdgeDivider';
import BackChevronIcon from '../components/shared/common/icons/BackChevronIcon';
import MenuToggleIcon from '../components/shared/common/icons/MenuToggleIcon';
import translationService from '../services/translator.service';

export const AppBarComponent = ({ userInfoReducer: { isLoggedIn }, languageReducer, setScreenItem, direction, onBackClick, onMenuToggle, isMenuOpen, onUserMenuStateChange, dividerInsetX = 16, logoOnly = false, hideBackButton = false, hideMenuButton = false, ...props }) => {
    const classes = useStyles();
    const theme = useTheme();
    const translatedData = translationService.getTranslatedData(languageReducer.language);
    const navigate = useNavigate();
    const runtimeDirection = direction || theme.direction;
    const isRtl = runtimeDirection === 'rtl';
    const shouldHideBackButton = logoOnly || hideBackButton;
    const shouldHideMenuButton = logoOnly || hideMenuButton;
    const [internalMenuOpen, setInternalMenuOpen] = useState(false);
    const isMenuControlled = typeof isMenuOpen === 'boolean';
    const resolvedMenuOpenState = useMemo(() => (isMenuControlled ? isMenuOpen : internalMenuOpen), [isMenuControlled, isMenuOpen, internalMenuOpen]);

    const handleBackClick = () => {
        if (onBackClick) {
            onBackClick();
            return;
        }

        navigate(-1);
    };

    const handleMenuToggleClick = () => {
        if (!isMenuControlled) {
            setInternalMenuOpen((previousState) => !previousState);
        }

        if (onMenuToggle) {
            onMenuToggle();
        }
    };

    const menuButton = shouldHideMenuButton ? (
        <Box className={classes.actionSpacer} />
    ) : onMenuToggle ? (
        <IconButton aria-label={translationService.getTranslation(translatedData, 'general.a11y.toggleMenu')} color='inherit' onClick={handleMenuToggleClick}>
            <MenuToggleIcon isOpen={resolvedMenuOpenState} className={classes.menuToggleIcon} />
        </IconButton>
    ) : isLoggedIn ? (
        <UserMenu cookies={props.cookies} setScreenItem={setScreenItem} onMenuStateChange={onUserMenuStateChange} />
    ) : (
        <Box className={classes.actionSpacer} />
    );

    const backButton = shouldHideBackButton ? (
        <Box className={classes.actionSpacer} />
    ) : (
        <IconButton aria-label={translationService.getTranslation(translatedData, 'general.a11y.goBack')} color='inherit' onClick={handleBackClick}>
            <BackChevronIcon className={classes.backIcon} />
        </IconButton>
    );

    const leftEdgeAction = isRtl ? menuButton : backButton;
    const rightEdgeAction = isRtl ? backButton : menuButton;

    return (
        <div className={classes.root}>
            <AppBar position='fixed' color='inherit' className={classes.appBar} elevation={0}>
                <Toolbar className={classes.toolbar}>
                    <Box className={classes.leftEdgeAction}>{leftEdgeAction}</Box>
                    <Box className={classes.logoWrap}>
                        <img src={logo} alt='Advice' className={classes.logo} />
                    </Box>
                    <Box className={classes.rightEdgeAction}>{rightEdgeAction}</Box>
                </Toolbar>
                <BottomEdgeDivider insetX={dividerInsetX} thickness={1} color='#000000' />
            </AppBar>
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    appBar: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        boxShadow: 'none',
        borderBottom: 'none',
    },
    logo: {
        height: '3.2vh',
        maxHeight: theme.spacing(4.5),
        width: 'auto',
    },
    toolbar: {
        minHeight: theme.spacing(8),
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    logoWrap: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    leftEdgeAction: {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: theme.spacing(7),
        minWidth: theme.spacing(7),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    rightEdgeAction: {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: theme.spacing(7),
        minWidth: theme.spacing(7),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    actionSpacer: {
        width: theme.spacing(5),
        height: theme.spacing(5),
    },
    backIcon: {
        fontSize: theme.typography.pxToRem(22),
        transform: 'none',
    },
    menuToggleIcon: {
        color: 'inherit',
    },
}));

AppBarComponent.propTypes = {
    userInfoReducer: PropTypes.object.isRequired,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
    logoOnly: PropTypes.bool,
    hideBackButton: PropTypes.bool,
    hideMenuButton: PropTypes.bool,
    onUserMenuStateChange: PropTypes.func,
    onBackClick: PropTypes.func,
    onMenuToggle: PropTypes.func,
    isMenuOpen: PropTypes.bool,
    dividerInsetX: PropTypes.number,
};

const mapStateToProps = (state) => ({
    userInfoReducer: state.userInfoReducer,
    languageReducer: state.languageReducer,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(AppBarComponent);
